import { useLazyQuery } from '@apollo/client';
import React, { FC, useEffect, useState } from 'react';
import { get, some, findIndex, map, differenceBy, concat, find, reduce, values, cloneDeep } from 'lodash';
import { endOfWeek, parseISO, formatISO } from 'date-fns';
import { Box, Text, Checkbox } from '@bechtel/gds';
import {
  WeeklyWorkInput,
  OwnershipLevel,
  MilestoneStatusCode,
  WeeklyWorkSummary,
  WeeklyWorkListSortByTerm,
  SortOrder,
  MilestoneType,
  CommodityType,
  WeeklyWorkListSearchBy,
  MilestoneTypeCount,
} from '@bechtel/types';
import { GET_WORKLIST_SUMMARY_MILESTONES } from '@bechtel/client-gql';
import { ListSelectionStyled, MilestoneListStyled, ContentStyled } from './styled';
import { MilestoneFilterTypes } from './types';
import { useDebounce } from '@bechtel/client-common';
import { WeekStartType } from '~/types';

export const MilestoneFilters: FC<MilestoneFilterTypes> = ({
  selectionStateValues,
  user,
  drawerValues,
  crewMember,
  searchQuery,
  onChange,
  weeklyWorkListItems,
}) => {
  const weekStartsOn: WeekStartType = (user?.project?.projectConfiguration.weekStartDay
    ? user?.project?.projectConfiguration.weekStartDay
    : 1) as WeekStartType; // Default start day is monday
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const currentWeekStartDay = currentProjectWeek?.startDate;
  const claimedFilter = selectionStateValues?.milestoneStatus === MilestoneStatusCode.CLAIMED ? true : false;
  const selectedWeekStartDay = selectionStateValues?.weekStartDay
    ? selectionStateValues?.weekStartDay
    : currentWeekStartDay;
  const debouncedSearchQuery = useDebounce(searchQuery?.trim(), 500);
  const [loadWorklistSummary, { data }] = useLazyQuery<
    {
      weeklyWorkList: WeeklyWorkSummary;
    },
    {
      input: WeeklyWorkInput;
    }
  >(GET_WORKLIST_SUMMARY_MILESTONES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!crewMember?.id || !crewMember?.persona || !currentWeekStartDay) return;
    const weekStartDate = selectedWeekStartDay instanceof Date ? selectedWeekStartDay : parseISO(selectedWeekStartDay);
    const worklistInput: WeeklyWorkInput = {
      crewMemberId: crewMember?.id,
      personaType: crewMember?.persona,
      startDate: formatISO(weekStartDate, { representation: 'date' }),
      endDate: formatISO(endOfWeek(weekStartDate, { weekStartsOn: weekStartsOn }), { representation: 'date' }),
      searchValue: debouncedSearchQuery,
      searchByField: drawerValues?.searchBy ? drawerValues?.searchBy : WeeklyWorkListSearchBy.TAG_ID,
      ownershipLevel: OwnershipLevel.PRIMARY, //TODO : Implement Secondary other once backend supports it
      workPackageIds: [],
      filterByMilestoneTypes: map(
        drawerValues?.filterByMilestoneTypes,
        (milestoneType: MilestoneType) => milestoneType.id
      ),
      filterByCommodityTypes: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
      filterByMilestoneStatus: claimedFilter ? MilestoneStatusCode.CLAIMED : MilestoneStatusCode.PLANNED,
      filterByPlanStatus: map(drawerValues?.filterByPlanStatusTypes, (item) => item.value),
      sortBy: WeeklyWorkListSortByTerm.MILESTONE_TYPE,
      sortOrder: SortOrder.ASC,
    };
    loadWorklistSummary({
      variables: {
        input: { ...worklistInput, filterByMilestoneTypes: [] },
      },
    });
  }, [
    loadWorklistSummary,
    weekStartsOn,
    user,
    currentWeekStartDay,
    selectedWeekStartDay,
    claimedFilter,
    drawerValues,
    debouncedSearchQuery,
    crewMember,
    weeklyWorkListItems,
  ]);

  const workSummary: WeeklyWorkSummary = get(data, 'weeklyWorkSummary');
  const { filterByMilestoneTypes } = drawerValues;
  const [milestoneFilters, setMilestoneFilters] = useState<MilestoneTypeCount[]>([]);

  useEffect(() => {
    if (!workSummary || !workSummary.milestoneCountsByType) return;
    const uniqueMilestoneCountsByType = reduce(
      workSummary.milestoneCountsByType,
      (milestoneList: any, milestoneCountsByTypeItem) => {
        if (!milestoneList[milestoneCountsByTypeItem.milestoneType.id]) {
          milestoneList[milestoneCountsByTypeItem.milestoneType.id] = cloneDeep(milestoneCountsByTypeItem);
        } else if (milestoneList[milestoneCountsByTypeItem.milestoneType.id]) {
          milestoneList[milestoneCountsByTypeItem.milestoneType.id].numMilestones += milestoneCountsByTypeItem.numMilestones;
        }
        return milestoneList;
      },
      []
    );
    const milestoneCountsByType = values(uniqueMilestoneCountsByType);
    const selectedMilestonesNotinFilters = differenceBy(
      filterByMilestoneTypes,
      map(milestoneCountsByType, (mct) => mct.milestoneType),
      'id'
    );
    if (!selectedMilestonesNotinFilters) {
      setMilestoneFilters(milestoneCountsByType);
      return;
    }
    const mergedFilters = concat(
      milestoneCountsByType,
      map(selectedMilestonesNotinFilters, (selectedFilter) => {
        return { milestoneType: selectedFilter, numMilestones: 0 };
      })
    );
    setMilestoneFilters(mergedFilters);
  }, [filterByMilestoneTypes, workSummary]);

  return (
    <ContentStyled data-testid="milestone-filters">
      {milestoneFilters && milestoneFilters.length > 0 && (
        <MilestoneListStyled flexDirection={{ sm: 'column' }}>
          <>
            {milestoneFilters.map((milestoneCount) => {
              const { milestoneType, numMilestones } = milestoneCount;
              return (
                <Box key={`${milestoneType.id}`}>
                  <ListSelectionStyled key={`${milestoneType.id}`}>
                    <Box mr={{ sm: '500' }}>
                      <Checkbox
                        data-testid={`search-by-${milestoneType.name}`}
                        checked={some(drawerValues?.filterByMilestoneTypes, (filterByMilestoneType) => {
                          return filterByMilestoneType.id === milestoneType.id;
                        })}
                        onChange={() => {
                          const selected = findIndex(filterByMilestoneTypes, (filterByMilestoneType) => {
                            return filterByMilestoneType.id === milestoneType.id;
                          });
                          if (selected !== -1) {
                            filterByMilestoneTypes.splice(selected, 1);
                          } else {
                            filterByMilestoneTypes.push(milestoneType);
                          }
                          onChange(filterByMilestoneTypes);
                        }}
                        readOnly
                      />
                    </Box>
                    <Text color="grey.1000" size="400">
                      {milestoneType.name}
                      {` (${numMilestones})`}
                    </Text>
                  </ListSelectionStyled>
                </Box>
              );
            })}
          </>
        </MilestoneListStyled>
      )}
    </ContentStyled>
  );
};
